/**
 * @file Quote management functions for Supabase
 * Provides strongly-typed functions for managing quotes in the application
 */

import { supabase } from "@/integrations/supabase/client";
import { Quote, QuotePhase } from "@/lib/types";
import { handleApiError, withErrorHandling, ErrorType } from "@/lib/errors";
import {
  CreateQuoteParams,
  BookVisitParams,
  SubmitPriceParams,
  DeclineQuoteParams,
  QuoteNotificationParams,
  ContactMethod
} from "@/lib/types/quote-types";
import { debug } from "@/lib/utils";
import { sendQuoteRequestNotification } from "./notification";

/**
 * Creates a new quote request
 * @param userId The ID of the user requesting the quote
 * @param params Quote data including trade_id and optional job_id
 * @returns The created quote or null if there was an error
 */
export const requestQuote = withErrorHandling(
  async (userId: string, params: CreateQuoteParams): Promise<Quote | null> => {
    // Validate params
    if (!params.trade_id) {
      throw new Error('Trade ID is required');
    }

    const { data: quote, error } = await supabase
      .from("quotes")
      .insert([{
        user_id: userId,
        job_id: params.job_id,
        trade_id: params.trade_id,
        phase: 'requested' as QuotePhase,
        created_at: new Date().toISOString()
      }])
      .select(`
        *,
        tradesperson:trade_id(*),
        job:job_id(*)
      `)
      .single();

    if (error) throw error;

    // Send notification for the new quote request
    try {
      if (quote) {
        await sendQuoteRequestNotification(quote as Quote);
      }
    } catch (notificationError) {
      // Log the error but don't fail the quote creation
      debug.error('Failed to send quote notification:', notificationError);
    }

    return quote as Quote;
  },
  null, // Return null on error
  'Failed to create quote request'
);

/**
 * Book a visit for a quote
 * @param params Parameters for booking a visit
 * @returns The updated quote or null if there was an error
 */
export const bookQuoteVisit = withErrorHandling(
  async (params: BookVisitParams): Promise<Quote | null> => {
    // Validate the date
    const visitDate = new Date(params.visitAt);
    if (isNaN(visitDate.getTime())) {
      throw new Error('Invalid date format');
    }

    // Make sure the visit is not in the past
    const now = new Date();
    if (visitDate < now) {
      throw new Error('Visit date cannot be in the past');
    }

    // Get the current quote to check status
    const { data: currentQuote, error: fetchError } = await supabase
      .from("quotes")
      .select("phase")
      .eq("id", params.quoteId)
      .single();

    if (fetchError) throw fetchError;

    // Validate that the quote is in a state that can be changed to visit_booked
    if (currentQuote.phase !== 'requested') {
      throw {
        message: 'Invalid quote transition: can only book visits for quotes in requested phase',
        type: ErrorType.QUOTE_INVALID_TRANSITION
      };
    }

    // Prepare update data
    type BookVisitUpdateData = {
      phase: QuotePhase;
      visit_at: string;
      notes?: string;
    };

    const updateData: BookVisitUpdateData = {
      phase: 'visit_booked',
      visit_at: params.visitAt
    };

    // Add additional notes if provided
    if (params.additionalNotes) {
      updateData.notes = params.additionalNotes;
    }

    const { data, error } = await supabase
      .from("quotes")
      .update(updateData)
      .eq("id", params.quoteId)
      .select(`
        *,
        tradesperson:trade_id(*),
        job:job_id(*)
      `)
      .single();

    if (error) throw error;
    return data as Quote;
  },
  null, // Return null on error
  'Failed to book visit'
);

/**
 * Submit a price for a quote
 * @param params Parameters for submitting a price
 * @returns The updated quote or null if there was an error
 */
export const submitQuotePrice = withErrorHandling(
  async (params: SubmitPriceParams): Promise<Quote | null> => {
    // Validate the price text - make sure it's not empty
    if (!params.priceText.trim()) {
      throw new Error('Price text cannot be empty');
    }

    // Get the current quote to check status
    const { data: currentQuote, error: fetchError } = await supabase
      .from("quotes")
      .select("phase")
      .eq("id", params.quoteId)
      .single();

    if (fetchError) throw fetchError;

    // Validate that the quote is in a state that can be priced
    const validPhases: QuotePhase[] = ['requested', 'visit_booked'];
    if (!validPhases.includes(currentQuote.phase)) {
      throw {
        message: 'Quote has already been priced or declined',
        type: ErrorType.QUOTE_ALREADY_PROCESSED
      };
    }

    // Prepare update data
    type PriceQuoteUpdateData = {
      phase: QuotePhase;
      price_text: string;
      updated_at: string;
      price_photo?: string;
      valid_until?: string;
    };

    const updateData: PriceQuoteUpdateData = {
      phase: 'priced',
      price_text: params.priceText,
      updated_at: new Date().toISOString()
    };

    // Add price photo if provided
    if (params.pricePhoto) {
      updateData.price_photo = params.pricePhoto;
    }

    // Add validity period if provided
    if (params.validityPeriod) {
      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + params.validityPeriod);
      updateData.valid_until = validUntil.toISOString();
    }

    const { data, error } = await supabase
      .from("quotes")
      .update(updateData)
      .eq("id", params.quoteId)
      .select(`
        *,
        tradesperson:trade_id(*),
        job:job_id(*)
      `)
      .single();

    if (error) throw error;
    return data as Quote;
  },
  null, // Return null on error
  'Failed to submit price'
);

/**
 * Decline a quote request
 * @param params Parameters for declining a quote
 * @returns The updated quote or null if there was an error
 */
export const declineQuote = withErrorHandling(
  async (params: DeclineQuoteParams): Promise<Quote | null> => {
    // Get the current quote to check status
    const { data: currentQuote, error: fetchError } = await supabase
      .from("quotes")
      .select("phase")
      .eq("id", params.quoteId)
      .single();

    if (fetchError) throw fetchError;

    // Validate that the quote is in a state that can be declined
    const validPhases: QuotePhase[] = ['requested', 'visit_booked'];
    if (!validPhases.includes(currentQuote.phase)) {
      throw {
        message: 'Quote has already been priced or declined',
        type: ErrorType.QUOTE_ALREADY_PROCESSED
      };
    }

    // Prepare update data
    type DeclineQuoteUpdateData = {
      phase: QuotePhase;
      rejection_reason: string;
      updated_at: string;
      suggest_alternative?: boolean;
      alternative_trade_id?: string;
    };

    const updateData: DeclineQuoteUpdateData = {
      phase: 'declined',
      rejection_reason: params.reason,
      updated_at: new Date().toISOString()
    };

    // Add suggestion flag if provided
    if (params.suggestAlternative) {
      updateData.suggest_alternative = true;
    }

    // Add alternative trade id if provided
    if (params.alternativeTradeId) {
      updateData.alternative_trade_id = params.alternativeTradeId;
    }

    const { data, error } = await supabase
      .from("quotes")
      .update(updateData)
      .eq("id", params.quoteId)
      .select(`
        *,
        tradesperson:trade_id(*),
        job:job_id(*)
      `)
      .single();

    if (error) throw error;
    return data as Quote;
  },
  null, // Return null on error
  'Failed to decline quote'
);

/**
 * Get all quotes for a user with enhanced filtering options
 * @param userId The ID of the user
 * @param options Optional filters and related data inclusion
 * @returns Array of quotes matching the filters
 */
export const getQuotesByUser = withErrorHandling(
  async (
    userId: string,
    options: {
      includeRelated?: boolean;
      phase?: QuotePhase | QuotePhase[];
      limit?: number;
      offset?: number;
      orderBy?: 'created_at' | 'updated_at' | 'visit_at';
      orderDirection?: 'asc' | 'desc';
    } = {}
  ): Promise<Quote[]> => {
    // Set defaults
    const {
      includeRelated = true,
      limit = 50,
      offset = 0,
      orderBy = 'created_at',
      orderDirection = 'desc'
    } = options;

    // Start building the query
    let query = supabase
      .from("quotes")
      .select(
        includeRelated
          ? `*, tradesperson:trade_id(*), job:job_id(*)`
          : '*'
      )
      .eq("user_id", userId)
      .order(orderBy, { ascending: orderDirection === 'asc' })
      .range(offset, offset + limit - 1);

    // Apply phase filter if provided
    if (options.phase) {
      if (Array.isArray(options.phase)) {
        query = query.in('phase', options.phase);
      } else {
        query = query.eq('phase', options.phase);
      }
    }

    // Execute the query
    const { data, error } = await query;

    if (error) throw error;
    return data as Quote[];
  },
  [], // Fallback value (empty array)
  'Failed to load quotes'
);

/**
 * Get all quotes for a job
 * @param jobId The ID of the job
 * @param options Optional filters
 * @returns Array of quotes for the job
 */
export const getQuotesByJob = withErrorHandling(
  async (
    jobId: string,
    options: {
      includeRelated?: boolean;
      phase?: QuotePhase | QuotePhase[];
    } = {}
  ): Promise<Quote[]> => {
    // Set defaults
    const { includeRelated = true } = options;

    // Start building the query
    let query = supabase
      .from("quotes")
      .select(
        includeRelated
          ? `*, tradesperson:trade_id(*), job:job_id(*)`
          : '*'
      )
      .eq("job_id", jobId)
      .order('created_at', { ascending: false });

    // Apply phase filter if provided
    if (options.phase) {
      if (Array.isArray(options.phase)) {
        query = query.in('phase', options.phase);
      } else {
        query = query.eq('phase', options.phase);
      }
    }

    // Execute the query
    const { data, error } = await query;

    if (error) throw error;
    return data as Quote[];
  },
  [], // Fallback value (empty array)
  'Failed to load job quotes'
);

/**
 * Get all quotes for a tradesperson
 * @param tradeId The ID of the tradesperson
 * @param options Optional filters
 * @returns Array of quotes for the tradesperson
 */
export const getQuotesByTrade = withErrorHandling(
  async (
    tradeId: string,
    options: {
      includeRelated?: boolean;
      phase?: QuotePhase | QuotePhase[];
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<Quote[]> => {
    // Set defaults
    const {
      includeRelated = true,
      limit = 50,
      offset = 0
    } = options;

    // Start building the query
    let query = supabase
      .from("quotes")
      .select(
        includeRelated
          ? `*, job:job_id(*)`
          : '*'
      )
      .eq("trade_id", tradeId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    // Apply phase filter if provided
    if (options.phase) {
      if (Array.isArray(options.phase)) {
        query = query.in('phase', options.phase);
      } else {
        query = query.eq('phase', options.phase);
      }
    }

    // Execute the query
    const { data, error } = await query;

    if (error) throw error;
    return data as Quote[];
  },
  [], // Fallback value (empty array)
  'Failed to load quotes'
);

/**
 * Get a single quote by ID with detailed error handling
 * @param quoteId The ID of the quote
 * @param includeRelated Whether to include related entities
 * @returns The quote or null if not found
 */
export const getQuoteById = withErrorHandling(
  async (
    quoteId: string,
    includeRelated: boolean = true
  ): Promise<Quote | null> => {
    // Build the query based on whether to include related entities
    const query = supabase
      .from("quotes")
      .select(
        includeRelated
          ? `*, tradesperson:trade_id(*), job:job_id(*)`
          : '*'
      )
      .eq("id", quoteId)
      .single();

    const { data, error } = await query;

    if (error) {
      // If the quote is not found, use the specific error type
      if (error.code === 'PGRST116' || error.message.includes('not found')) {
        throw {
          type: ErrorType.QUOTE_NOT_FOUND,
          message: 'Quote not found',
          originalError: error
        };
      }
      throw error;
    }
    return data as Quote;
  },
  null, // Fallback value
  'Failed to load quote'
);

/**
 * Delete a quote by ID
 * @param quoteId The ID of the quote
 * @returns True if deleted successfully
 */
export const deleteQuote = withErrorHandling(
  async (quoteId: string): Promise<boolean> => {
    // Validate parameter
    if (!quoteId) {
      throw new Error('Quote ID is required');
    }

    const { error } = await supabase
      .from("quotes")
      .delete()
      .eq("id", quoteId);

    if (error) throw error;
    return true;
  },
  false, // Return false on error
  'Failed to delete quote'
);

/**
 * Deletes all quotes associated with a job
 * Note: This function is now only needed for backward compatibility
 * as cascade delete is implemented in the database
 *
 * @param jobId The ID of the job
 * @returns True if all quotes were deleted successfully
 */
export const deleteQuotesByJob = withErrorHandling(
  async (jobId: string): Promise<boolean> => {
    debug.log('deleteQuotesByJob is deprecated due to cascade delete being implemented.');

    // Validate parameter
    if (!jobId) {
      throw new Error('Job ID is required');
    }

    const { error } = await supabase
      .from("quotes")
      .delete()
      .eq("job_id", jobId);

    if (error) throw error;
    return true;
  },
  false, // Return false on error
  'Failed to delete job quotes'
);
